import React, { type FC, useCallback } from "react";
import { connect, type ConnectedProps } from "react-redux";
import Restricted from "features/Application/Restricted";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import ContentUsersNoResults from "features/Library/Common/Fields/ContentUsersNoResults";
import { SearchInput } from "components";
import { AssignmentPeopleContext, RolePermissions, SortingDirection } from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { useContentAssignmentsPermissions } from "hooks/useContentAssignmentsPermissions";
import { fetchExternalLinkUsers } from "../state/thunks/externalLinkQueriesThunk";
import { type ColumnOption } from "interfaces/columnOptions";
import UserInfoCell from "components/userInfoCell/UserInfoCell";
import { type FiltersMap } from "utils/filterUtils";
import { type LinkUser } from "../types/models";

interface UsersOverviewOwnProps {
  linkId: number;
  customHeaderContent: React.ReactElement;
}

type Props = UsersOverviewOwnProps & PropsFromRedux;

const columnOptions: ColumnOption<LinkUser>[] = [
  {
    name: "Name",
    width: 5,
    isSortable: true,
    disabled: (item) => item.inherited,
    render: (item) => <UserInfoCell deepLink={!item.inherited} user={item} />,
  },
];

const ColumnToParamMap: {
  [key: string]: string;
} = {
  name: "firstName",
};

const UsersOverview: FC<Props> = ({ linkId, customHeaderContent, users, fetchExternalLinkUsers: fetchUsers }) => {
  const permissionPredicate = useContentAssignmentsPermissions(
    AssignmentPeopleContext.User,
    RolePermissions.AssetsManage,
  );

  const getLinkUsers = useCallback(
    (
      skip: number = 0,
      top: number = 10,
      sortColumn: string = "Name",
      sortDirection: SortingDirection = SortingDirection.Descending,
      appliedFilter: FiltersMap = {},
    ) => {
      fetchUsers(linkId, skip, top, ColumnToParamMap[sortColumn] ?? "firstName", sortDirection, appliedFilter);
    },
    [fetchUsers, linkId],
  );

  return (
    <Restricted
      permissions={[]}
      permissionPredicate={permissionPredicate}
      renderContent={() => (
        <GenericItemsView
          items={users.items}
          isLoading={users.isLoading}
          fetchData={getLinkUsers}
          dataCount={users.count}
          columnOptions={columnOptions}
          renderSearch={() => <SearchInput placeholder="Search for Users..." disabled />}
          customHeaderContent={customHeaderContent}
          noResultsContent={<ContentUsersNoResults filtered={false} />}
        />
      )}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  users: state.library.externalLinks.details.users,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchExternalLinkUsers: bindAction(fetchExternalLinkUsers, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedUsersOverview = connector(UsersOverview);

export default ConnectedUsersOverview;
